// src/ChatBox.js
import React, { useState, useRef, useEffect } from "react";
import "./ChatBox.css";
import { API_BASE } from "./config";

function ChatBox({ conv_id, messages = [], onSendMessage, theme, disabled }) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const chatRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const postAsk = async (question) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 saniye

    try {
      const res = await fetch(`${API_BASE}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conv_id, question }),
        signal: controller.signal,
      });

      if (!res.ok) {
        let serverMsg = "";
        try {
          const j = await res.json();
          serverMsg = j?.detail || j?.message || "";
        } catch (_) {}
        if (res.status === 429) throw new Error("Hız limiti aşıldı (429). Birazdan tekrar deneyin.");
        if (res.status === 400) throw new Error(serverMsg || "Geçersiz istek (400).");
        if (res.status === 401) throw new Error("Yetkilendirme hatası (401). API anahtarını kontrol edin.");
        if (res.status === 500) throw new Error("Sunucu hatası (500). Biraz sonra tekrar deneyin.");
        throw new Error(serverMsg || `İstek başarısız (HTTP ${res.status}).`);
      }

      const data = await res.json();
      // Backend farklı alan isimleri dönebilir; güvenli yakalama:
      const assistantText =
        data?.answer ??
        data?.message ??
        data?.result ??
        (typeof data === "string" ? data : JSON.stringify(data));

      return assistantText || "Boş yanıt döndü.";
    } catch (err) {
      if (err.name === "AbortError") {
        throw new Error("İstek zaman aşımına uğradı. Lütfen tekrar deneyin.");
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || !conv_id) return;

    setErrMsg("");
    setSending(true);
    setInput("");

    // 1) Kullanıcı mesajını hemen listeye ekleyelim (UI hızlı hissettirsin)
    onSendMessage?.({ sender: "user", text });

    try {
      // 2) Backend'e sor
      const answer = await postAsk(text);

      // 3) Bot yanıtını ekle
      onSendMessage?.({ sender: "bot", text: answer });
    } catch (e) {
      const msg = e.message || "Bir hata oluştu.";
      setErrMsg(msg);
      // Hata mesajını da mesaja dönüştürüp gösterelim (opsiyonel)
      onSendMessage?.({ sender: "bot", text: `⚠️ ${msg}` });
      console.error("Chat error:", e);
    } finally {
      setSending(false);
    }
  };

  const isDisabled = disabled || sending || !conv_id;

  return (
    <div className={`gpt-chatbox ${theme}`}>
      <div className="gpt-messages" ref={chatRef}>
        {messages.length === 0 && (
          <div className="gpt-chat-empty">Henüz mesaj yok.</div>
        )}
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`gpt-message ${msg.sender === "user" ? "right" : "left"}`}
          >
            <span className="gpt-badge">
              {msg.sender === "user" ? "🧑‍💻" : "🤖"}
            </span>
            <span className="gpt-bubble">{msg.text}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {errMsg && (
        <div className="upload-error-alert" style={{ margin: "8px 0" }}>
          {errMsg}
        </div>
      )}

      <div className="gpt-input-area">
        <input
          type="text"
          className="gpt-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            !conv_id
              ? "Önce bir PDF yükleyin…"
              : sending
              ? "Yanıt bekleniyor…"
              : "Bir şey yaz..."
          }
          disabled={isDisabled}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <button
          className="gpt-send-btn"
          onClick={handleSend}
          disabled={isDisabled || input.trim() === ""}
          title={!conv_id ? "Önce PDF yükleyin" : "Gönder"}
        >
          {sending ? "…" : "➤"}
        </button>
      </div>
    </div>
  );
}

export default ChatBox;